/**
 * HTML Sanitization Utilities for Ticket Frontend
 *
 * Provides functions to prevent XSS attacks by escaping user input
 * before inserting into HTML templates.
 */

/**
 * Escape HTML special characters to prevent XSS
 *
 * @param {*} str - Value to escape (will be converted to string)
 * @returns {string} Escaped string safe for HTML content
 */
export function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Escape string for use in HTML attributes
 * Additionally escapes newlines which can break attribute values
 *
 * @param {*} str - Value to escape
 * @returns {string} Escaped string safe for HTML attributes
 */
export function sanitizeForAttribute(str) {
  return escapeHtml(str).replace(/\n/g, '&#10;').replace(/\r/g, '&#13;');
}

/**
 * Validate that a value is a safe integer (for IDs, positions, etc.)
 *
 * @param {*} value - Value to validate
 * @param {number} fallback - Fallback value if invalid
 * @returns {number} Safe integer value
 */
export function safeInt(value, fallback = 0) {
  const num = parseInt(value, 10);
  if (Number.isNaN(num) || !Number.isFinite(num)) {
    return fallback;
  }
  return num;
}

/**
 * Validate that a string looks like a valid Frappe document name
 * (alphanumeric, hyphens, underscores only)
 *
 * @param {*} str - Value to validate
 * @returns {string} Validated string or empty string if invalid
 */
export function safeDocName(str) {
  if (str == null) return '';
  const s = String(str);
  // Frappe document names: alphanumeric, hyphens, underscores, spaces
  if (/^[\w\s-]+$/.test(s) && s.length <= 140) {
    return s;
  }
  return '';
}

/**
 * Validate email format (basic check)
 *
 * @param {*} email - Email to validate
 * @returns {string} Email if valid format, empty string otherwise
 */
export function safeEmail(email) {
  if (email == null) return '';
  const s = String(email).trim();
  // Basic email pattern - not exhaustive but catches obvious issues
  if (/^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(s) && s.length <= 254) {
    return escapeHtml(s);
  }
  return '';
}

/**
 * Create a safe URL for href attributes
 * Prevents javascript: and data: protocol attacks
 *
 * @param {*} url - URL to validate
 * @returns {string} Safe URL or '#' if invalid
 */
export function safeUrl(url) {
  if (url == null) return '#';
  const s = String(url).trim();

  // Block dangerous protocols
  const lower = s.toLowerCase();
  if (lower.startsWith('javascript:') || lower.startsWith('data:') || lower.startsWith('vbscript:')) {
    return '#';
  }

  // Allow relative URLs, http, https, mailto
  if (s.startsWith('/') || s.startsWith('http://') || s.startsWith('https://') || s.startsWith('mailto:')) {
    return encodeURI(s);
  }

  // For other URLs, be cautious
  return '#';
}

export default {
  escapeHtml,
  sanitizeForAttribute,
  safeInt,
  safeDocName,
  safeEmail,
  safeUrl,
};
